import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Layers, Loader2 } from "lucide-react";
import type { ApiResponse, PublicConfig } from "@/types";

export function LoginPage() {
  const [emailOrUsername, setEmailOrUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [config, setConfig] = useState<PublicConfig | null>(null);

  const { login, isAuthenticated } = useAuth();
  const { error: toastError, success: toastSuccess } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/dashboard", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    api
      .get<ApiResponse<PublicConfig>>("/api/auth/public-config")
      .then((res) => {
        if (res && res.data) {
          setConfig(res.data);
        }
      })
      .catch(() => {});
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailOrUsername.trim() || !password) {
      toastError("Validation Error", "Please enter your email/username and password.");
      return;
    }

    setSubmitting(true);
    try {
      await login(emailOrUsername.trim(), password, rememberMe);
      toastSuccess("Welcome Back", "Authentication successful.");
      navigate("/dashboard");
    } catch (err: any) {
      toastError("Login Failed", err.message || "Invalid credentials.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative isolate min-h-dvh w-full overflow-hidden bg-[#efefef] px-4 py-6 sm:px-8 sm:py-10">
      <div aria-hidden="true" className="login-dot-background pointer-events-none absolute inset-0" />
      <main className="relative z-10 mx-auto flex min-h-[calc(100dvh_-_3rem)] w-full max-w-6xl items-center justify-center sm:min-h-[calc(100dvh_-_5rem)]">
        <section className="grid w-full overflow-hidden rounded-xl border border-slate-200 bg-white shadow-[0_18px_55px_-32px_rgba(15,23,42,0.28)] md:min-h-[620px] md:grid-cols-[1.08fr_0.92fr]">
          <div className="flex flex-col justify-between border-b border-[#484848] bg-[#363636] p-7 text-white sm:p-10 md:border-b-0 md:border-r md:p-12 lg:p-14">
            <div>
              <div className="flex items-center gap-3">
                <span className="flex size-10 items-center justify-center rounded-lg bg-[#e06d20] text-white shadow-sm">
                  <Layers className="size-5" />
                </span>
                <div>
                  <p className="text-xs font-extrabold tracking-[0.14em] text-white">
                    {(config?.shortName || "NELGATES").toUpperCase()}
                  </p>
                  <p className="mt-1 text-[9px] font-bold uppercase tracking-[0.2em] text-slate-300">Textile CRM</p>
                </div>
              </div>

              <div className="mt-14 max-w-md sm:mt-20 md:mt-28">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#f08a42]">
                  Textile sales operations
                </p>
                <h1 className="mt-4 text-3xl font-semibold leading-tight tracking-tight text-white sm:text-4xl">
                  From first enquiry to confirmed order.
                </h1>
                <p className="mt-5 max-w-sm text-sm leading-6 text-slate-300">
                  Keep buyer relationships, sampling, quotations and order follow-through together in one workspace.
                </p>
              </div>
            </div>

            <div className="mt-12 border-t border-white/15 pt-5">
              <p className="text-xs font-medium text-slate-300">
                BUYERS <span className="mx-2 text-[#e06d20]">/</span> ENQUIRIES <span className="mx-2 text-[#e06d20]">/</span> SAMPLES <span className="mx-2 text-[#e06d20]">/</span> ORDERS
              </p>
              <p className="mt-4 text-xs text-slate-400">
                &copy; {new Date().getFullYear()} {config?.name || "Nelgates Textile CRM"}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-center px-7 py-10 sm:px-12 md:px-10 lg:px-16">
            <div className="w-full max-w-sm">
              <header className="mb-8">
                <h2 className="text-2xl font-semibold tracking-tight text-slate-900">Sign in to your account</h2>
                <p className="mt-2 text-sm text-slate-500">Use your work email or username to continue.</p>
              </header>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                    Email or username
                  </Label>
                  <Input
                    id="email"
                    type="text"
                    value={emailOrUsername}
                    onChange={(e) => setEmailOrUsername(e.target.value)}
                    className="h-11 border-slate-300 bg-white text-sm shadow-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/15 autofill:bg-white"
                    autoComplete="username"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                      Password
                    </Label>
                    <Link
                      to="/forgot-password"
                      className="text-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 rounded-sm"
                    >
                      Forgot password?
                    </Link>
                  </div>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-11 border-slate-300 bg-white text-sm shadow-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/15 autofill:bg-white"
                    autoComplete="current-password"
                    required
                  />
                </div>

                <label htmlFor="rememberMe" className="flex cursor-pointer items-center gap-2.5 py-1 text-sm text-slate-600">
                  <input
                    id="rememberMe"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className="size-4 rounded border-slate-300 accent-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                  />
                  Keep me signed in
                </label>

                <Button type="submit" disabled={submitting} className="mt-2 h-11 w-full font-medium">
                  {submitting ? <Loader2 className="size-4 animate-spin" /> : "Sign in"}
                </Button>
              </form>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
